import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { CarrierForm } from "@/components/CarrierForm";
import { CarrierList } from "@/components/CarrierList";
import api from "@/lib/api";
import { toast } from "sonner";

const empty = { name: "", tier: 2, location: "", notes: "", website: "", contacts: [], preferred: false, disabled: false };
const emptyContact = { name: "", email: "", phone: "" };
const EMAIL_RE = /^\S+@\S+\.\S+$/;

export default function CarrierAdmin({ open, onOpenChange, carriers, reload }) {
  const [editing, setEditing] = useState(null); // carrier id or "new"
  const [form, setForm] = useState(empty);

  const startNew = () => {
    setForm(empty);
    setEditing("new");
  };
  const startEdit = (c) => {
    const withKeys = (list) => list.map((ct) => ({ ...ct, _key: ct._key || Math.random().toString(36).slice(2) }));
    setForm({
      name: c.name,
      tier: c.tier,
      location: c.location,
      notes: c.notes,
      website: c.website || "",
      contacts: withKeys(c.contacts?.length ? c.contacts : c.email ? [{ name: "", email: c.email, phone: "" }] : []),
      preferred: c.preferred || false,
      disabled: c.disabled,
    });
    setEditing(c.id);
  };
  const cancel = () => {
    setEditing(null);
    setForm(empty);
  };

  const updContact = (idx, k, v) =>
    setForm({ ...form, contacts: form.contacts.map((ct, i) => (i === idx ? { ...ct, [k]: v } : ct)) });
  const addContact = () =>
    setForm({ ...form, contacts: [...form.contacts, { ...emptyContact, _key: Math.random().toString(36).slice(2) }] });
  const removeContact = (idx) => setForm({ ...form, contacts: form.contacts.filter((_, i) => i !== idx) });

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    const contacts = form.contacts
      .filter((ct) => ct.name.trim() || ct.email.trim() || ct.phone.trim())
      .map(({ _key, ...ct }) => ct);
    const badEmail = contacts.find((ct) => ct.email.trim() && !EMAIL_RE.test(ct.email.trim()));
    if (badEmail) {
      toast.error(`Invalid contact email: ${badEmail.email}`);
      return;
    }
    const firstEmail = contacts.map((ct) => ct.email.trim()).find((e) => EMAIL_RE.test(e)) || "";
    const payload = { ...form, contacts, email: firstEmail, tier: Number(form.tier) };
    try {
      if (editing === "new") await api.post("/carriers", payload);
      else await api.put(`/carriers/${editing}`, payload);
      toast.success("Carrier saved");
      cancel();
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/carriers/${id}`);
      toast.success("Carrier removed");
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-[#1C1917]">Carrier Directory</DialogTitle>
          <DialogDescription>Add, edit, or remove carriers grouped by tier.</DialogDescription>
        </DialogHeader>

        {editing ? (
          <CarrierForm
            form={form}
            setForm={setForm}
            updContact={updContact}
            addContact={addContact}
            removeContact={removeContact}
            save={save}
            cancel={cancel}
          />
        ) : (
          <Button onClick={startNew} className="bg-[#1C1917] hover:bg-black text-white self-start" data-testid="carrier-add-button">
            <Plus className="h-4 w-4 mr-1.5" /> Add carrier
          </Button>
        )}

        <CarrierList carriers={carriers} onEdit={startEdit} onDelete={remove} />
      </DialogContent>
    </Dialog>
  );
}
